// Guided Clavian Boot setup (spec/13 §13.1, F-BOOT-29): the five steps and how each knows it is done. Every step is worked out
// from what the venue has — the boot server, its images, its PCs — never marked by hand, so a step ticks the moment the
// thing happens, wherever it was done. Pure (no I/O), so each rule is tested on its own.
//
//   server         the venue's boot server is paired and online
//   networkStart   the boot server has seen a PC start from the network (any PC, known or not), or a PC has started from it
//   image          a Windows (system) image has a published version
//   games          a game disk has a published version
//   pcs            at least one boot PC is linked to a station

import type { BootFloorPc, BootImage, BootNetworkStartSeen, BootNewPc, BootServer } from '@clavian/api-client';

export const BOOT_SETUP_STEPS = ['server', 'networkStart', 'image', 'games', 'pcs'] as const;
export type BootSetupStepId = (typeof BOOT_SETUP_STEPS)[number];

/** The only step a venue may leave out: not every venue puts games on a shared disk. */
export const SKIPPABLE_BOOT_STEPS: readonly BootSetupStepId[] = ['games'];

export interface BootSetupFacts {
  readonly servers: readonly BootServer[];
  readonly images: readonly BootImage[];
  /** Every PC linked to a station (`GET …/boot/floor`). */
  readonly floorPcs: readonly BootFloorPc[];
  /** Every PC that started from the boot server and is not a station yet (`GET …/boot/new-pcs`). */
  readonly newPcs: readonly BootNewPc[];
  /** Now, in ms: what "not seen for N minutes" counts from. */
  readonly now: number;
}

export interface BootStepStatus {
  readonly id: BootSetupStepId;
  readonly done: boolean;
  /** What is wrong, as a copy key under `bootGuide.setup` with its values; null when nothing is. */
  readonly stuck: { readonly key: string; readonly values: Readonly<Record<string, string | number>> } | null;
}

const live = (image: Pick<BootImage, 'archivedAt' | 'currentVersionId'>): boolean => image.archivedAt === null && image.currentVersionId !== null;

/** The newest network start the boot server saw, from any PC; null from an older boot server or before any. */
export function lastNetworkStartOf(servers: readonly BootServer[]): BootNetworkStartSeen | null {
  return servers[0]?.lastNetworkStart ?? null;
}

export function bootSetupSteps(facts: BootSetupFacts): BootStepStatus[] {
  const server = facts.servers[0];
  const online = server?.status === 'online';
  const system = facts.images.filter((image) => image.kind === 'system' && live(image));
  const games = facts.images.filter((image) => image.kind === 'game' && live(image));
  const started = lastNetworkStartOf(facts.servers) !== null || facts.floorPcs.some((pc) => pc.lastStartAt !== null) || facts.newPcs.some((pc) => pc.lastStartAt !== null);

  let serverStuck: BootStepStatus['stuck'] = null;
  if (server !== undefined && !online) {
    if (server.status === 'pairing') serverStuck = { key: 'server.stuckPairing', values: {} };
    else if (server.lastSeenAt === null) serverStuck = { key: 'server.stuckNever', values: {} };
    else serverStuck = { key: 'server.stuckOffline', values: { count: Math.max(1, Math.floor((facts.now - Date.parse(server.lastSeenAt)) / 60_000)) } };
  }

  return [
    { id: 'server', done: online, stuck: serverStuck },
    { id: 'networkStart', done: started, stuck: null },
    { id: 'image', done: system.length > 0, stuck: null },
    { id: 'games', done: games.length > 0, stuck: null },
    { id: 'pcs', done: facts.floorPcs.length > 0, stuck: facts.floorPcs.length === 0 && facts.newPcs.length > 0 ? { key: 'pcs.waiting', values: { count: facts.newPcs.length } } : null },
  ];
}

/** The image a step shows: the first system image with a published version, or the game disk. */
export function firstLive(images: readonly BootImage[], kind: 'system' | 'game'): BootImage | undefined {
  return images.find((image) => image.kind === kind && live(image));
}

/** The network start the boot server saw after `baseline` (what it had seen when a test began); null while there is none. */
export function networkStartSince(servers: readonly BootServer[], baseline: string | null): BootNetworkStartSeen | null {
  const seen = lastNetworkStartOf(servers);
  if (seen === null) return null;
  if (baseline === null) return seen;
  return Date.parse(seen.at) > Date.parse(baseline) ? seen : null;
}

/** How long a test waits for a network start before it says nothing arrived. */
export const NETWORK_TEST_WAIT_MS = 90_000;

// -- Skips: this device only ------------------------------------------------------------------------------------------
//
// The server's setup steps keep their skips in the venue (`PUT …/setup/skips/{step}`), but it has no Clavian Boot steps, so
// a skipped boot step is remembered in this browser, per venue. Another device shows the step as to do.

const skipKey = (venueId: string): string => `clavian.setup.bootSkips.${venueId}`;

export function readBootSkips(venueId: string): ReadonlySet<BootSetupStepId> {
  try {
    const raw = window.localStorage.getItem(skipKey(venueId));
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is BootSetupStepId => SKIPPABLE_BOOT_STEPS.includes(id as BootSetupStepId)) : []);
  } catch {
    return new Set();
  }
}

export function writeBootSkips(venueId: string, skips: ReadonlySet<BootSetupStepId>): void {
  try {
    window.localStorage.setItem(skipKey(venueId), JSON.stringify([...skips]));
  } catch {
    // A private window: the skip lasts until the page closes.
  }
}
